import { useState, useEffect, useRef } from "react";
import { LuCopy } from "react-icons/lu";

import {
  getReferralStats,
  generateReferralCode,
  getEarnings,
  getSales,
  getRegistrations,
  deactivateCode,
} from "../../api/referral";

import {
  INVALID_CODE_TYPE_MESSAGE,
  filterCodeTypeInput,
  isUsableCodeType,
  buildReferralUrl,
} from "../../utils/referralCodeUtils";

import Card from "../shared/Card";
import Button from "../shared/Button";
import Swal from "sweetalert2";
import { PuffLoader } from "react-spinners";
import { MdDeleteOutline } from "react-icons/md";

// Los módulos de src/api relanzan `error.response.data` tal cual, así que aquí
// puede llegar un texto, un objeto del backend o un Error de red.
const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  return error.message || error.detail || error.title || fallback;
};

const ReferralStats = () => {
  const [earnings, setEarnings] = useState([]);
  const [sales, setSales] = useState([]);
  const [registration, setRegistration] = useState([]);

  const [referralCodes, setReferralCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referralType, setReferralType] = useState("");

  const referralInputRef = useRef(null);
  // Posición en la que hay que dejar el cursor después de filtrar lo escrito o
  // lo pegado, para no mandarlo al final del texto.
  const caretPositionRef = useRef(null);

  // ¿Hay algo utilizable que enviar? De ello dependen el botón y el error.
  const canGenerate = isUsableCodeType(referralType);

  // Único error del campo: se escribió algo que no deja ninguna letra ni número
  // (solo espacios). Los demás caracteres inválidos ya no entran al campo.
  const showFieldError = referralType.length > 0 && !canGenerate;

  // React repinta el input con el valor ya filtrado; sin esto el cursor
  // saltaría al final al escribir o pegar en medio del texto.
  useEffect(() => {
    const input = referralInputRef.current;
    const caret = caretPositionRef.current;

    if (input && caret !== null) {
      input.setSelectionRange(caret, caret);
      caretPositionRef.current = null;
    }
  }, [referralType]);

  const loadStats = async () => {
    try {
      const codes = await getReferralStats();

      if (Array.isArray(codes)) {
        setReferralCodes(codes);
      } else {
        setReferralCodes([]);
      }

      const totalEarnings = await getEarnings();

      setEarnings(totalEarnings);

      const totalSales = await getSales();
      setSales(totalSales);

      const totalRegistrations = await getRegistrations();
      setRegistration(totalRegistrations);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar las estadísticas",
      });
      console.error("Error al cargar las estadísticas:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleGenerateCode = async () => {
    // Red de seguridad: con el botón deshabilitado no se debería llegar aquí.
    if (!canGenerate) return;

    try {
      const response = await generateReferralCode({
        type: referralType.trim(),
        // Ya no enviamos commission_rate
      });

      if (response.code) {
        await loadStats();
        // Mostramos el código y el tipo tal como los devolvió el servidor, no
        // el texto que escribió el usuario ni la vista previa del cliente.
        Swal.fire({
          icon: "success",
          title: "¡Código generado!",
          text: response.code_type
            ? `Tu nuevo código es: ${response.code} (tipo: ${response.code_type})`
            : `Tu nuevo código es: ${response.code}`,
        });
        setReferralType("");
      }
    } catch (error) {
      // El backend responde 400 si el tipo no deja nada utilizable. Con el
      // campo filtrado no debería ocurrir, pero se muestra su mensaje.
      Swal.fire({
        icon: "error",
        title: "Error",
        text: getErrorMessage(error, "Error al generar el código"),
      });
    }
  };

  // Filtra lo que se escribe o se pega: los caracteres que el backend
  // descartaría no llegan a aparecer en el campo, y se corta al llegar al
  // límite. El cursor se queda donde estaba.
  const handleInputChange = (e) => {
    const input = e.target;
    const rawValue = input.value;
    const selectionStart = input.selectionStart ?? rawValue.length;

    const value = filterCodeTypeInput(rawValue);
    const caret = Math.min(
      filterCodeTypeInput(rawValue.slice(0, selectionStart)).length,
      value.length,
    );

    // Si el texto filtrado coincide con el que ya estaba en el estado, React no
    // vuelve a pintar; se corrige el input en el acto para que el carácter
    // rechazado no se quede escrito.
    if (value !== rawValue) {
      input.value = value;
    }
    input.setSelectionRange(caret, caret);
    caretPositionRef.current = caret;

    setReferralType(value);
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(buildReferralUrl(code));
    Swal.fire({
      icon: "success",
      title: "¡Copiado!",
      text: "Enlace copiado al portapapeles",
      timer: 1500,
    });
  };

  const handleDeactivateCode = (code) => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: `¿Deseas desactivar el código ${code}? Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, desactivar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deactivateCode(code);
          await loadStats();
          Swal.fire({
            icon: "success",
            title: "Desactivado",
            text: `El código ${code} ha sido desactivado correctamente`,
            timer: 2000,
          });
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Error al desactivar el código",
          });
          console.error("Error al desactivar el código:", error);
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="loader-container">
        <PuffLoader
          color="#009ee3"
          loading={true}
          size={60}
          margin={2}
          speedMultiplier={4}
        />
      </div>
    );
  }

  return (
    <div className="referral-stats">
      <div className="referral-stats__header">
        <h2>Estadísticas de Referidos</h2>
        <div className="generate-code-form">
          <div className="generate-code-form__field">
            <input
              type="text"
              placeholder="Código de Referido"
              value={referralType}
              onChange={handleInputChange}
              className={`referral-type-input ${
                showFieldError ? "referral-type-input--invalid" : ""
              }`}
              ref={referralInputRef}
              aria-invalid={showFieldError}
              aria-describedby={
                showFieldError ? "referral-type-error" : undefined
              }
              style={{
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
              }}
            />
            {showFieldError && (
              <p
                id="referral-type-error"
                className="generate-code-form__error"
                aria-live="polite"
              >
                {INVALID_CODE_TYPE_MESSAGE}
              </p>
            )}
          </div>
          {/* El cursor de "no permitido" va en el contenedor: un <button>
              deshabilitado no recibe eventos de puntero y no siempre aplica su
              propio cursor. */}
          <span
            className={`generate-code-form__action ${
              canGenerate ? "" : "generate-code-form__action--disabled"
            }`}
          >
            <Button onClick={handleGenerateCode} disabled={!canGenerate}>
              Generar Nuevo Código
            </Button>
          </span>
        </div>
      </div>

      <div className="referral-stats__grid">
        <Card className="stat-card">
          <h3>Registros</h3>
          <p className="stat-value">{registration}</p>
        </Card>
        <Card className="stat-card">
          <h3>Ventas</h3>
          <p className="stat-value">{sales || 0}</p>
        </Card>
        <Card className="stat-card">
          <h3>Ganancias COP</h3>
          <p className="stat-value">{earnings}</p>
        </Card>
      </div>

      <Card title="Tus Códigos de Referido" className="referral-stats__codes">
        <div className="referral-codes">
          {referralCodes.length === 0 ? (
            <p>No tienes códigos de referido aún.</p>
          ) : (
            [...referralCodes]
              .sort((a, b) => {
                if (a.is_active !== b.is_active) {
                  return a.is_active ? -1 : 1;
                }
                return new Date(b.created_at) - new Date(a.created_at);
              })
              .map((code) => {
                const referralUrl = buildReferralUrl(code.code);
                return (
                  <div
                    style={{
                      background: code.is_active ? "#edf4ff" : "#ffebee",
                      border: code.is_active
                        ? "1px solid rgba(0, 158, 227, 0.2)"
                        : "1px solid rgba(244, 67, 54, 0.2)",
                    }}
                    key={code.code}
                    className="referral-code"
                  >
                    <div className="referral-code__info">
                      <h4>
                        {code.code}{" "}
                        <span className="code-type">({code.code_type})</span>
                      </h4>
                      <p
                        className={`status ${
                          code.is_active ? "active" : "inactive"
                        }`}
                      >
                        {code.is_active ? "Activo" : "Inactivo"}
                      </p>
                      <p>Clicks: {code.clicks ?? 0}</p>
                      <p>Registros: {code.registrations ?? 0}</p>
                      <p>
                        Comisión primera venta:{" "}
                        {code.first_payment_commission_rate || 10}%
                      </p>
                      <p>
                        Comisión ventas siguientes:{" "}
                        {code.following_payments_commission_rate || 5}%
                      </p>
                      <p>Ganancias: ${code.earnings?.toFixed(2) ?? "0.00"}</p>
                      <p>
                        Creado: {new Date(code.created_at).toLocaleString()}
                      </p>

                      <p>
                        Enlace:{" "}
                        <a
                          href={referralUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="referral-code__link"
                          style={{ color: code.is_active ? "" : "#f44336" }}
                        >
                          {referralUrl}
                        </a>
                      </p>

                      {code.is_active && (
                        <p className="commission-explanation-highlight">
                          Ganas {code.first_payment_commission_rate || 10}% en
                          la primera compra de cada referido, y{" "}
                          {code.following_payments_commission_rate || 5}% en
                          todas sus compras posteriores.
                        </p>
                      )}
                    </div>
                    {code.is_active && (
                      <div className="referral-code__buttons">
                        <Button
                          variant="outline--copy"
                          onClick={() => copyToClipboard(code.code)}
                        >
                          Copiar Enlace <LuCopy />
                        </Button>
                        <Button
                          variant="outline--delete"
                          onClick={() => handleDeactivateCode(code.code)}
                        >
                          Desactivar <MdDeleteOutline size={20} />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })
          )}
        </div>
      </Card>
    </div>
  );
};

export default ReferralStats;
